import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import DashBoardTable from "./reuseable-components/DashBoardTable";
import {
  downloadCSV,
  downloadExcel,
  downloadPDF,
} from "../../utils/exportUtils";

const formatDonationDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-CA");
};

const DonationsTab = ({ donations = [], summary = {} }) => {
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const columns = [
    { id: "donor", label: "Donor", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone", sortable: false },
    { id: "address", label: "Address", sortable: false },
    { id: "amount", label: "Amount", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "date", label: "Date", sortable: true },
  ];

  /* =========================================
      ORIGINAL DATA
  ========================================= */
  const donationRows = useMemo(
    () =>
      donations.map((d) => ({
        donor: `${d.firstName} ${d.lastName}`,
        email: d.email || "-",
        phone: d.phone || "-",
        address: d.address || "-",
        amount: `$${(d.amount || 0).toLocaleString()}`,
        status: d.paymentStatus || "pending",
        date: formatDonationDate(d.paidAt || d.createdAt),
      })),
    [donations],
  );

  /* =========================================
      SORTING
  ========================================= */

  const sortedRows = useMemo(() => {
    let data = [...donationRows];

    if (sortConfig.key) {
      data.sort((a, b) => {
        let valueA = a[sortConfig.key];
        let valueB = b[sortConfig.key];

        // Amount sorting
        if (sortConfig.key === "amount") {
          valueA = Number(valueA.replace(/[$,]/g, ""));
          valueB = Number(valueB.replace(/[$,]/g, ""));
        }

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }

        return 0;
      });
    }

    return data;
  }, [donationRows, sortConfig]);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /* =========================================
      PAGINATION
  ========================================= */

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  /* =========================================
      FINAL TABLE ROWS
  ========================================= */

  const rows = paginatedRows.map((row) => ({
    ...row,

    donor: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          {row.donor}
        </Typography>
      </Box>
    ),

    amount: (
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "13px",
        }}
      >
        {row.amount}
      </Typography>
    ),

    status: (
      <Box
        sx={{
          display: "inline-block",
          px: 1.5,
          py: 0.5,
          borderRadius: "12px",
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          bgcolor: row.status === "completed" ? "#E8F5E9" : "#FFF3E0",
          color: row.status === "completed" ? "#2E7D32" : "#EF6C00",
        }}
      >
        {row.status}
      </Box>
    ),
  }));

  return (
    <Box
      className="donations-tab-container"
      sx={{
        p: {
          xs: 1,
          md: 3,
        },
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Box
        className="tab-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#0F1E3D",
          }}
        >
          Donations
        </Typography>

        <>
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleExportClick}
            sx={{
              bgcolor: "#D4A017",
              "&:hover": {
                bgcolor: "#B8860B",
              },
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              px: 3,
            }}
          >
            Export
          </Button>

          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              onClick={() => {
                downloadCSV({
                  data: sortedRows,
                  fileName: "donations",
                });
                handleClose();
              }}
            >
              Download CSV
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadExcel({
                  data: sortedRows,
                  fileName: "donations",
                  sheetName: "Donations",
                });
                handleClose();
              }}
            >
              Download Excel
            </MenuItem>

            <MenuItem
              onClick={() => {
                downloadPDF({
                  data: sortedRows,
                  fileName: "donations",
                  title: "Donations",
                  columns,
                });
                handleClose();
              }}
            >
              Download PDF
            </MenuItem>
          </Menu>
        </>
      </Box>

      <Box
        sx={{
          borderBottom: "1px solid #EAE2D1",
          mb: 3,
        }}
      />

      {/* Stats Cards Row using Box Flex */}
      <Box
        className="stats-container"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
        }}
      >
        {[
          {
            label: "TOTAL RAISED",
            value: `$${(summary.totalRaised || 0).toLocaleString()}`,
          },
          {
            label: "DONATIONS",
            value: summary.count || 0,
          },
          {
            label: "UNIQUE DONORS",
            value: summary.uniqueDonors || 0,
          },
          {
            label: "AVG GIFT",
            value: `$${(summary.avgGift || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
        ].map((stat, idx) => (
          <Box
            key={idx}
            sx={{
              flex: "1 1 calc(25% - 16px)",
              minWidth: "200px",
            }}
          >
            <Card
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #EAE2D1",
                bgcolor: "#F8F9FA",
              }}
            >
              <CardContent
                sx={{
                  p: "16px !important",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#777",
                    fontWeight: 700,
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: "#0F1E3D",
                    mt: 0.5,
                  }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Table Section */}
      <Box className="table-section" sx={{ mt: 2 }}>
        <DashBoardTable
          columns={columns}
          rows={rows}
          sortConfig={sortConfig}
          onSort={(key) =>
            setSortConfig((prev) => ({
              key,
              direction:
                prev.key === key && prev.direction === "asc" ? "desc" : "asc",
            }))
          }
          page={page}
          totalPages={Math.ceil(sortedRows.length / rowsPerPage)}
          onPageChange={(value) => setPage(value)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          totalResults={sortedRows.length}
        />
      </Box>
    </Box>
  );
};

export default DonationsTab;
